import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import { Send, Brain, User, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

export default function CoachPage() {
  const { user, getAuthHeader, API } = useAuth();
  const { i18n } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/coach/history`, { headers: getAuthHeader() });
      const history = response.data.reverse().flatMap(item => [
        { role: 'user', content: item.user_message, timestamp: item.created_at },
        { role: 'coach', content: item.coach_response, timestamp: item.created_at }
      ]);
      setMessages(history);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setHistoryLoaded(true);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date().toISOString() }]);
    setLoading(true);

    const lang = i18n.language === 'no' ? 'no' : 'en';
    
    try {
      const response = await axios.post(
        `${API}/coach/chat?lang=${lang}`,
        { message: userMessage },
        { headers: getAuthHeader() }
      );
      setMessages(prev => [...prev, { role: 'coach', content: response.data.response, timestamp: response.data.timestamp }]);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to get response from coach');
      setMessages(prev => prev.slice(0, -1));
      setInput(userMessage);
    } finally {
      setLoading(false);
    }
  };

  const suggestedPrompts = [
    "How can I overcome performance anxiety?",
    "Help me visualize success",
    "I need help staying focused",
    "How do I bounce back from failure?"
  ];

  return (
    <Layout>
      <div className="h-[calc(100vh-8rem)] lg:h-[calc(100vh-4rem)] flex flex-col" data-testid="coach-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-serif font-bold text-2xl md:text-3xl text-foreground">AI Mental Coach</h1>
          <p className="text-muted-foreground">Your personal guide to peak mental performance</p>
        </div>

        {/* Chat Area */}
        <Card className="flex-1 glass flex flex-col overflow-hidden">
          <ScrollArea ref={scrollRef} className="flex-1 p-4 md:p-6">
            {messages.length === 0 && historyLoaded ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6">
                <div className="p-4 rounded-full bg-primary/20 mb-4">
                  <Brain className="h-12 w-12 text-primary" />
                </div>
                <h2 className="font-serif text-2xl text-foreground mb-2">Welcome, {user?.name?.split(' ')[0]}</h2>
                <p className="text-muted-foreground mb-6 max-w-md">
                  I'm your mental performance coach. Ask me anything about visualization, focus, overcoming anxiety, or building mental resilience.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg">
                  {suggestedPrompts.map((prompt, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      className="text-left h-auto py-3 px-4 border-white/10 hover:border-primary/30 hover:bg-primary/5"
                      onClick={() => setInput(prompt)}
                      data-testid={`suggested-prompt-${i}`}
                    >
                      {prompt}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <AnimatePresence>
                  {messages.map((msg, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`flex items-start gap-3 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                        <div className={`p-2 rounded-full shrink-0 ${msg.role === 'user' ? 'bg-primary/20' : 'bg-secondary/20'}`}>
                          {msg.role === 'user' ? (
                            <User className="h-5 w-5 text-primary" />
                          ) : (
                            <Brain className="h-5 w-5 text-secondary" />
                          )}
                        </div>
                        <div
                          className={`p-5 rounded-2xl ${
                            msg.role === 'user'
                              ? 'bg-primary/10 border border-primary/20'
                              : 'bg-card border border-white/10'
                          }`}
                        >
                          <p className={`text-foreground whitespace-pre-wrap ${
                            msg.role === 'coach' 
                              ? 'text-base leading-loose tracking-wide' 
                              : 'leading-relaxed'
                          }`} style={msg.role === 'coach' ? { lineHeight: '1.9', letterSpacing: '0.01em' } : {}}>
                            {msg.content}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {loading && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-secondary/20">
                        <Brain className="h-5 w-5 text-secondary" />
                      </div>
                      <div className="p-4 rounded-2xl bg-card border border-white/10">
                        <Loader2 className="h-5 w-5 text-secondary animate-spin" />
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Input Area */}
          <div className="p-4 border-t border-white/10">
            <form onSubmit={sendMessage} className="flex gap-3">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask your coach anything..."
                className="flex-1 bg-input/50 border-transparent focus:border-primary"
                disabled={loading}
                data-testid="coach-input"
              />
              <Button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6"
                data-testid="coach-send-btn"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
